import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel, SportModel } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { GridModel } from '@frontend/sports/grid/core/feature/model';
import { ObservableGrid } from '@frontend/sports/grid/core/feature/observables';
import { getEvents } from '@frontend/sports/grid/core/feature/store';
import { sum } from 'lodash-es';
import { Observable, combineLatest, map } from 'rxjs';

export type UpcomingLoader = () => Observable<{ grid: ObservableGrid; count: number }[]>;

export type UpcomingMoreLoader = (skip: number, sport: SportModel) => Observable<EventModel[]>;

export interface UpcomingModel {
    load: UpcomingLoader;
    more: UpcomingMoreLoader;
}

export class UpcomingGrid {
    loading: boolean;

    constructor(
        public grid: ObservableGrid,
        private count: number,
    ) {
        this.loading = false;
    }

    get more(): boolean {
        return this.count > getEvents(this.grid.getState()).length;
    }
}

@Component({
    selector: 'ms-live-upcoming-list',
    templateUrl: 'live-upcoming-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LiveUpcomingListComponent implements OnChanges {
    @Input() model: UpcomingModel;
    @Input() tracking: string;
    @Input() loadOnScroll = false;

    upcoming: UpcomingGrid[];
    loading: boolean;
    totalUpcomingLoadedEventsCount = 0;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private changeRef: ChangeDetectorRef,
    ) {}

    ngOnChanges(changes: ISimpleChanges<LiveUpcomingListComponent>): void {
        if (changes.model) {
            this.getUpcoming();
        }
    }

    getMoreUpcoming(upcoming: UpcomingGrid): void {
        if (!upcoming.more || upcoming.loading) {
            return;
        }

        upcoming.loading = true;
        this.changeRef.markForCheck();

        this.model
            .more(getEvents(upcoming.grid.getState()).length, upcoming.grid.getState().sport)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((events) => {
                upcoming.grid.addEvents(events);
                upcoming.loading = false;
                this.changeRef.markForCheck();
            });
    }

    getLoadedEventsCountForGrid(gridModel: GridModel): number {
        return getEvents(gridModel).length;
    }

    private getUpcoming(): void {
        this.loading = true;
        this.model
            .load()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((upcoming) => {
                this.upcoming = upcoming.map((sport) => new UpcomingGrid(sport.grid, sport.count));
                this.loading = false;
                this.changeRef.markForCheck();

                combineLatest(upcoming.map((sportGrid) => sportGrid.grid.pipe(map((gridModel) => getEvents(gridModel).length))))
                    .pipe(
                        map((gridEventsCounts) => sum(gridEventsCounts)),
                        takeUntilDestroyed(this.destroyRef),
                    )
                    .subscribe((sumAllLoadedGridEventCounts) => {
                        this.totalUpcomingLoadedEventsCount = sumAllLoadedGridEventCounts;
                        this.changeRef.markForCheck();
                    });
            });
    }
}
